import { useEffect, useState } from "react";
import axios from "axios";
import {
  FaUserFriends,
  FaCheckCircle,
  FaHourglassHalf,
  FaChevronCircleLeft,
} from "react-icons/fa";
import { FaCircleChevronRight } from "react-icons/fa6";
import {
  FiCamera,
  FiX,
  FiCheck,
  FiRefreshCw,
  FiAlertTriangle,
} from "react-icons/fi";
import { useToast } from "@chakra-ui/react";
import { db } from "../lib/firebase";
import {
  collection,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";

// Mock data array for fallback testing
const DUMMY_SELFIE_USERS = [];

const SelfieApprovals = () => {
  const [loading, setLoading] = useState(true);
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [stats, setStats] = useState({
    totalSubmissions: 0,
    pendingReview: 0,
    approved: 0,
    rejected: 0,
    totalUsers: 0,
  });

  const [selectedUser, setSelectedUser] = useState(null);
  const [modalAction, setModalAction] = useState(null); // 'review' | 'rejected-view'
  const [photoApprovals, setPhotoApprovals] = useState([]); // [{ photoIndex, isApproved: boolean | null, feedback: string }]
  const [adminNotes, setAdminNotes] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(25);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();

  // Change to 4 for production requirement
  const MIN_PHOTOS_REQUIRED = 1;

  const fetchStatistics = async () => {
    try {
      const res = await axios.get("/api/v1/admin/photo-verification/statistics");
      if (res.data?.status === "OK" || res.data?.success) {
        const data = res.data?.data?.statistics || res.data?.data || {};
        setStats({
          totalSubmissions: data?.total?.withPhotoUpload || data?.totalSubmissions || 0,
          pendingReview: data?.verificationStatus?.pending?.count || data?.pendingReview || 0,
          approved: data?.verificationStatus?.approved?.count || data?.approved || 0,
          rejected: data?.verificationStatus?.rejected?.count || data?.rejected || 0,
          totalUsers: data?.total?.femaleUsers || data?.totalUsers || 0,
        });
      }
    } catch (_) {
      const pending = DUMMY_SELFIE_USERS?.filter((u) => u.selfieVerificationStatus === "pending").length;
      const approved = DUMMY_SELFIE_USERS?.filter((u) => u.selfieVerificationStatus === "approved").length;
      const rejected = DUMMY_SELFIE_USERS?.filter((u) => u.selfieVerificationStatus === "rejected").length;
      setStats({
        totalSubmissions: DUMMY_SELFIE_USERS?.length || 0,
        pendingReview: pending,
        approved: approved,
        rejected: rejected,
        totalUsers: DUMMY_SELFIE_USERS?.length || 0,
      });
    }
  };

  const fetchUsersByStatus = async (status = "all", pageNumber = 1) => {
    try {
      setLoading(true);
      const res = await axios.get(
        `/api/v1/admin/photo-verification/users?status=${status}&page=${pageNumber}&limit=${limit}`
      );

      if (res.data?.status === "OK") {
        const users = res.data?.data?.users || [];
        const pagination = res.data?.data?.pagination || { totalPages: 1 };

        if (users.length > 0) {
          setPendingVerifications(users);
          setTotalPages(pagination.totalPages || 1);
          return;
        }
      }
      throw new Error("No live backend users found, falling back to mock data");
    } catch (_) {
      const filtered = selectedFilter === "all"
        ? DUMMY_SELFIE_USERS
        : DUMMY_SELFIE_USERS?.filter((u) => u.selfieVerificationStatus === status);

      setPendingVerifications(filtered);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsersByStatus(selectedFilter, page);
    fetchStatistics();
  }, [selectedFilter, page, limit]);

  const openReviewModal = (user) => {
    setSelectedUser(user);
    setModalAction("review");
    setAdminNotes("");

    // Initialize photo state: preserve previous decision if re-reviewing, else default to null
    const initialApprovals = (user.verificationPhotos || []).map((p, idx) => ({
      photoIndex: idx,
      isApproved: p.adminStatus === "approved" ? true : p.adminStatus === "rejected" ? false : null,
      feedback: p.adminFeedback || "",
    }));
    setPhotoApprovals(initialApprovals);
  };

  const handleVerificationSubmit = async () => {
    if (!selectedUser || isSubmitting) return;

    // Verify all photos have received a decision
    const unreviewedIndex = photoApprovals.findIndex((p) => p.isApproved === null);
    if (unreviewedIndex !== -1) {
      toast({
        title: "Incomplete Review",
        description: `Please approve or reject Photo ${unreviewedIndex + 1} before submitting.`,
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const approvedCount = photoApprovals.filter((p) => p.isApproved === true).length;
    const totalPhotos = selectedUser.verificationPhotos?.length || photoApprovals.length;
    const isApproved = approvedCount >= MIN_PHOTOS_REQUIRED;
    const finalStatus = isApproved ? "approved" : "rejected";

    const payload = {
      decision: finalStatus,
      status: finalStatus,
      adminNotes: adminNotes,
      photoApprovals: photoApprovals.map((p) => ({
        photoIndex: p.photoIndex,
        isApproved: p.isApproved,
        status: p.isApproved ? "approved" : "rejected",
        feedback: p.feedback || "",
      })),
    };

    try {
      setIsSubmitting(true);

      // Backend API Call
      await axios.post(
        `/api/v1/admin/photo-verification/verify/${selectedUser._id || selectedUser.id}`,
        payload
      );

      // Firestore Audit Trail
      try {
        await addDoc(collection(db, "photo_verifications"), {
          userId: selectedUser._id || selectedUser.id,
          userName: selectedUser.fullName || "",
          photoApprovals: payload.photoApprovals,
          adminNotes: adminNotes,
          approvedCount: approvedCount,
          totalPhotos: totalPhotos,
          decision: finalStatus,
          verifiedAt: serverTimestamp(),
        });
      } catch (fsErr) {
        console.error("Firestore verification audit log failed:", fsErr);
      }

      // Update Local State for the targeted user
      setPendingVerifications((prev) =>
        prev.map((u) => {
          const matchId = (u.id && u.id === (selectedUser.id || selectedUser._id)) ||
                          (u._id && u._id === (selectedUser.id || selectedUser._id));
          if (!matchId) return u;

          return {
            ...u,
            overallPhotoVerificationStatus: finalStatus,
            verificationStatus: finalStatus,
            approvedPhotos: approvedCount,
            rejectedPhotos: totalPhotos - approvedCount,
            verificationPhotos: u.verificationPhotos?.map((photo, idx) => ({
              ...photo,
              adminStatus: photoApprovals[idx]?.isApproved ? "approved" : "rejected",
              adminFeedback: photoApprovals[idx]?.feedback || photo.adminFeedback,
            })),
          };
        })
      );

      // Update Top Metrics Counter
      setStats((prev) => ({
        ...prev,
        pendingReview: Math.max(0, prev.pendingReview - 1),
        approved: isApproved ? prev.approved + 1 : prev.approved,
        rejected: !isApproved ? prev.rejected + 1 : prev.rejected,
      }));

      toast({
        title: isApproved ? "Photos Approved" : "Photos Rejected",
        description: isApproved
          ? `${approvedCount}/${totalPhotos} photo(s) passed. Account marked approved.`
          : `Rejected (${approvedCount}/${totalPhotos} passed). User must resubmit.`,
        status: isApproved ? "success" : "info",
        duration: 3500,
        isClosable: true,
      });

      // Close modal and clean up state
      setSelectedUser(null);
      setModalAction(null);
      setPhotoApprovals([]);
      setAdminNotes("");
    } catch (error) {
      toast({
        title: "Verification Failed",
        description: error.response?.data?.message || "An error occurred while saving the decision.",
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const statCards = [
    {
      title: "Total Users",
      value: stats.totalUsers,
      icon: <FaUserFriends />,
      color: "from-sky-500/20 to-blue-600/10",
      border: "border-sky-400/30",
      text: "text-sky-300",
    },
    {
      title: "Selfie Submissions",
      value: stats.totalSubmissions,
      icon: <FiCamera />,
      color: "from-cyan-500/20 to-teal-500/10",
      border: "border-cyan-400/30",
      text: "text-cyan-300",
    },
    {
      title: "Pending Review",
      value: stats.pendingReview,
      icon: <FaHourglassHalf />,
      color: "from-amber-500/20 to-orange-500/10",
      border: "border-amber-400/30",
      text: "text-amber-300",
    },
    {
      title: "Approved",
      value: stats.approved,
      icon: <FaCheckCircle />,
      color: "from-teal-500/20 to-emerald-500/10",
      border: "border-teal-400/30",
      text: "text-teal-300",
    },
    {
      title: "Rejected",
      value: stats.rejected,
      icon: <FiAlertTriangle />,
      color: "from-rose-500/20 to-pink-500/10",
      border: "border-rose-400/30",
      text: "text-rose-300",
    },
  ];

  return (
    <div className="space-y-6 text-cyan-50 p-2 md:p-4">
      {/* Header with Filter */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-2 border-b border-cyan-500/20">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-cyan-200 via-sky-300 to-white">
            Photo Verification
          </h1>
          <p className="text-xs sm:text-sm text-cyan-200/60 mt-1">
            Review user submitted profile photos to confirm identity and profile authenticity
          </p>
        </div>

        {/* Status Filter Dropdown */}
        <div className="relative w-full sm:w-56">
          <select
            value={selectedFilter}
            onChange={(e) => {
              setSelectedFilter(e.target.value);
              setPage(1);
            }}
            className="w-full appearance-none bg-cyan-950/70 border border-cyan-400/30 text-cyan-100 font-semibold text-xs rounded-xl px-4 py-2.5 pr-8 focus:outline-none focus:border-cyan-300 backdrop-blur-md shadow-inner cursor-pointer"
          >
            <option className="bg-[#030e28] text-white" value="pending">⏳ Pending Review</option>
            <option className="bg-[#030e28] text-white" value="approved">✅ Approved</option>
            <option className="bg-[#030e28] text-white" value="rejected">❌ Rejected</option>
            <option className="bg-[#030e28] text-white" value="all">📁 All Submissions</option>
          </select>
          <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none text-cyan-400">
            ▼
          </div>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {statCards.map((stat, idx) => (
          <div
            key={idx}
            className={`rounded-2xl p-4 bg-gradient-to-br ${stat.color} border ${stat.border} backdrop-blur-xl shadow-lg transition-transform hover:-translate-y-1`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-cyan-200/70">
                  {stat.title}
                </p>
                <p className={`text-2xl font-black mt-1 ${stat.text}`}>
                  {Number(stat.value || 0).toLocaleString()}
                </p>
              </div>
              <div className={`text-2xl ${stat.text} opacity-80`}>{stat.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Main Review Grid */}
      <div
        className="rounded-2xl border border-cyan-500/20 p-5 backdrop-blur-xl"
        style={{
          background: "linear-gradient(180deg, rgba(6, 24, 60, 0.75) 0%, rgba(3, 13, 36, 0.9) 100%)",
          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.45)",
        }}
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-cyan-500/20 border border-cyan-400/30 text-cyan-300">
              <FiCamera size={18} />
            </div>
            <div>
              <h2 className="text-base font-bold text-white tracking-wide">
                Photo Submissions ({pendingVerifications.length})
              </h2>
              <p className="text-xs text-cyan-200/60">Review submitted verification photos against user profiles</p>
            </div>
          </div>
          <button
            onClick={() => {
              fetchUsersByStatus(selectedFilter, page);
              fetchStatistics();
            }}
            className="flex items-center gap-1.5 text-xs text-cyan-300 hover:text-white px-3 py-1.5 rounded-lg bg-cyan-950/60 border border-cyan-400/30 hover:bg-cyan-900/60 transition"
          >
            <FiRefreshCw size={13} />
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 rounded-full border-4 border-cyan-500/20 border-t-cyan-400 animate-spin" />
          </div>
        ) : pendingVerifications.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {pendingVerifications.map((user) => {
              const status = (user.overallPhotoVerificationStatus || user.verificationStatus || "pending").toLowerCase();
              const totalPhotos = user.totalPhotos || user.verificationPhotos?.length || 0;
              const approvedPhotos = user.approvedPhotos || user.verificationPhotos?.filter((p) => p.adminStatus === "approved").length || 0;
              const rejectedPhotos = user.rejectedPhotos !== undefined
                ? user.rejectedPhotos
                : user.verificationPhotos?.filter((p) => p.adminStatus === "rejected").length || 0;

              return (
                <div
                  key={user.id || user._id}
                  className="rounded-2xl p-4 flex flex-col justify-between border border-cyan-500/20 bg-cyan-950/30 hover:border-cyan-400/40 transition-all duration-300 shadow-lg"
                >
                  <div>
                    {/* Verification Photos Preview Bar */}
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      {(user.verificationPhotos || []).slice(0, 3).map((photo, idx) => (
                        <div key={idx} className="relative group">
                          <div className="w-full aspect-square rounded-lg overflow-hidden border border-cyan-500/30 bg-[#020b1e] flex items-center justify-center relative">
                            <img
                              src={photo.url}
                              alt={`Photo ${idx + 1}`}
                              className="w-full h-full object-cover group-hover:scale-105 transition"
                            />
                            <div className="absolute top-1 right-1 text-[10px] px-1.5 py-0.2 rounded-full font-bold border backdrop-blur-md">
                              {photo.adminStatus === "approved" ? (
                                <span className="text-teal-300">✓</span>
                              ) : photo.adminStatus === "rejected" ? (
                                <span className="text-rose-300">✕</span>
                              ) : (
                                <span className="text-amber-300">?</span>
                              )}
                            </div>
                          </div>
                          <p className="text-[10px] text-cyan-300/70 mt-1 text-center truncate">Photo {idx + 1}</p>
                        </div>
                      ))}
                    </div>

                    {/* Stats Counter */}
                    <div className="grid grid-cols-3 gap-2 text-center text-xs mb-3">
                      <div className="p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
                        <p className="text-cyan-300/70 text-[11px]">Total</p>
                        <p className="font-bold text-cyan-200">{totalPhotos}</p>
                      </div>
                      <div className="p-2 rounded-lg bg-teal-500/10 border border-teal-500/20">
                        <p className="text-teal-300/70 text-[11px]">Approved</p>
                        <p className="font-bold text-teal-200">{approvedPhotos}</p>
                      </div>
                      <div className="p-2 rounded-lg bg-rose-500/10 border border-rose-500/20">
                        <p className="text-rose-300/70 text-[11px]">Rejected</p>
                        <p className="font-bold text-rose-200">{rejectedPhotos}</p>
                      </div>
                    </div>

                    {/* Member Details */}
                    <div className="space-y-1 mb-4">
                      <div className="flex items-center justify-between">
                        <h3 className="font-bold text-sm text-white truncate">{user.fullName || "Unnamed User"}</h3>
                        <span
                          className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border ${
                            status === "approved"
                              ? "bg-teal-500/15 border-teal-400/30 text-teal-300"
                              : status === "rejected"
                              ? "bg-rose-500/15 border-rose-400/30 text-rose-300"
                              : "bg-amber-500/15 border-amber-400/30 text-amber-300"
                          }`}
                        >
                          {status}
                        </span>
                      </div>
                      <p className="text-xs text-cyan-200/70">
                        {user.gender || "Gender not specified"} • DOB: {user.dateOfBirth ? new Date(user.dateOfBirth).toLocaleDateString() : "N/A"}
                      </p>
                      <p className="text-[11px] text-cyan-300/50 font-mono truncate">
                        {user.phoneNumber || user.email || "No contact info"}
                      </p>
                      {user.createdAt && (
                        <p className="text-[10px] text-cyan-400/40">
                          Submitted: {new Date(user.createdAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Actions Bar */}
                  <div className="pt-3 border-t border-cyan-500/15 flex items-center gap-2">
                    {status === "approved" ? (
                      <div className="w-full flex items-center justify-between gap-2">
                        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-teal-500/15 border border-teal-400/40 text-teal-300 text-xs font-bold">
                          <FiCheck className="text-teal-400 stroke-[3]" />
                          <span>Approved</span>
                        </div>
                        <button
                          onClick={() => openReviewModal(user)}
                          className="px-3 py-1.5 rounded-xl text-xs font-medium text-amber-300 bg-amber-950/40 border border-amber-500/25 hover:bg-amber-900/40 transition"
                        >
                          Re-review
                        </button>
                      </div>
                    ) : status === "rejected" ? (
                      <div className="w-full flex items-center justify-between gap-2">
                        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-rose-500/15 border border-rose-400/40 text-rose-300 text-xs font-bold">
                          <FiX className="text-rose-400 stroke-[3]" />
                          <span>Rejected</span>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setModalAction("rejected-view");
                            }}
                            className="px-3 py-1.5 rounded-xl text-xs font-medium text-rose-300 bg-rose-950/40 border border-rose-500/25 hover:bg-rose-900/40 transition"
                          >
                            Feedback
                          </button>
                          <button
                            onClick={() => openReviewModal(user)}
                            className="px-3 py-1.5 rounded-xl text-xs font-medium text-cyan-300 bg-cyan-950/40 border border-cyan-500/25 hover:bg-cyan-900/40 transition"
                          >
                            Re-review
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => openReviewModal(user)}
                        className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-cyan-500 to-blue-600 hover:brightness-110 shadow-md transition"
                      >
                        <FiCheck size={14} /> Review Photos
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <FiCamera className="mx-auto text-4xl text-cyan-400/40 mb-3" />
            <p className="text-sm font-semibold text-cyan-200">No photo submissions found</p>
            <p className="text-xs text-cyan-400/50 mt-1">All profiles have been reviewed or match the selected filter</p>
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-8 pt-4 border-t border-cyan-500/15">
            <div className="flex items-center gap-2">
              <span className="text-xs text-cyan-200/70">Per page:</span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                className="bg-cyan-950/70 border border-cyan-400/30 text-cyan-100 text-xs rounded-lg px-2 py-1"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="p-2 rounded-lg bg-cyan-950/60 border border-cyan-400/30 text-cyan-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-cyan-900/50 transition"
              >
                <FaChevronCircleLeft />
              </button>
              <span className="text-xs text-cyan-200 px-2 font-medium">
                Page {page} of {totalPages}
              </span>
              <button
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="p-2 rounded-lg bg-cyan-950/60 border border-cyan-400/30 text-cyan-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-cyan-900/50 transition"
              >
                <FaCircleChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Review Modal */}
      {selectedUser && modalAction === "review" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md overflow-y-auto">
          <div
            className="w-full max-w-2xl rounded-2xl overflow-hidden border border-cyan-400/30 backdrop-blur-2xl my-8 flex flex-col max-h-[90vh]"
            style={{ background: "rgba(4, 18, 48, 0.95)" }}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-cyan-500/20 bg-cyan-950/40">
              <div>
                <h3 className="text-base font-bold text-white">
                  Review Photos — {selectedUser.fullName || "User"}
                </h3>
                <p className="text-xs text-cyan-300/60 mt-0.5">
                  Require at least {MIN_PHOTOS_REQUIRED} approved photo(s) to pass verification
                </p>
              </div>
              <button
                onClick={() => {
                  setSelectedUser(null);
                  setModalAction(null);
                  setPhotoApprovals([]);
                }}
                className="text-cyan-300 hover:text-white"
              >
                <FiX size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div className="space-y-4">
                {(selectedUser.verificationPhotos || []).map((photo, idx) => {
                  const approval = photoApprovals[idx] || { photoIndex: idx, isApproved: null, feedback: "" };

                  return (
                    <div key={idx} className="border border-cyan-500/20 rounded-xl p-4 bg-cyan-950/40">
                      <div className="flex flex-col sm:flex-row gap-4">
                        {/* Image Preview */}
                        <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden border border-cyan-500/30 bg-[#020b1e]">
                          <img
                            src={photo.url}
                            alt={`Photo ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>

                        {/* Controls */}
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                const updated = [...photoApprovals];
                                updated[idx] = { ...approval, isApproved: true };
                                setPhotoApprovals(updated);
                              }}
                              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                                approval.isApproved === true
                                  ? "bg-teal-500/30 border border-teal-400 text-teal-300 ring-1 ring-teal-400/50"
                                  : "bg-cyan-950/60 border border-cyan-400/30 text-cyan-300 hover:bg-cyan-900/60"
                              }`}
                            >
                              ✓ Approve
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const updated = [...photoApprovals];
                                updated[idx] = { ...approval, isApproved: false };
                                setPhotoApprovals(updated);
                              }}
                              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                                approval.isApproved === false
                                  ? "bg-rose-500/30 border border-rose-400 text-rose-300 ring-1 ring-rose-400/50"
                                  : "bg-cyan-950/60 border border-cyan-400/30 text-cyan-300 hover:bg-cyan-900/60"
                              }`}
                            >
                              ✕ Reject
                            </button>
                            <span className="text-xs text-cyan-300/60 ml-auto font-mono">
                              Photo #{idx + 1}
                            </span>
                          </div>

                          <input
                            type="text"
                            value={approval.feedback}
                            onChange={(e) => {
                              const updated = [...photoApprovals];
                              updated[idx] = { ...approval, feedback: e.target.value };
                              setPhotoApprovals(updated);
                            }}
                            placeholder={
                              approval.isApproved === false
                                ? "Rejection reason (e.g., blurry, face not visible)..."
                                : "Notes (optional)..."
                            }
                            className="w-full bg-cyan-950/60 border border-cyan-400/30 rounded-lg px-3 py-1.5 text-xs text-white placeholder-cyan-300/50 focus:outline-none focus:border-cyan-300"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Admin Notes */}
              <div className="pt-4 border-t border-cyan-500/20">
                <label className="block text-xs font-semibold uppercase text-cyan-200 mb-2">
                  Admin Internal Notes (Optional)
                </label>
                <textarea
                  rows={2}
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Additional audit notes about this user or submission..."
                  className="w-full bg-cyan-950/60 border border-cyan-400/30 rounded-lg px-3 py-2 text-xs text-white placeholder-cyan-300/50 focus:outline-none focus:border-cyan-300"
                />
              </div>

              {/* Dynamic Scorecard */}
              {(() => {
                const total = photoApprovals.length;
                const approvedCount = photoApprovals.filter((p) => p.isApproved === true).length;
                const meetsRequirement = approvedCount >= MIN_PHOTOS_REQUIRED;

                return (
                  <div className="pt-2">
                    <div className="grid grid-cols-3 gap-3 text-center text-sm">
                      <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
                        <p className="text-cyan-300/70 text-xs">Total Photos</p>
                        <p className="text-xl font-bold text-cyan-300">{total}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-teal-500/10 border border-teal-500/20">
                        <p className="text-teal-300/70 text-xs">Approved</p>
                        <p className="text-xl font-bold text-teal-300">{approvedCount}</p>
                      </div>
                      <div
                        className={`p-3 rounded-lg border transition ${
                          meetsRequirement
                            ? "bg-teal-500/10 border-teal-400/30 text-teal-300"
                            : "bg-rose-500/10 border-rose-400/30 text-rose-300"
                        }`}
                      >
                        <p className="text-xs opacity-80">Calculated Verdict</p>
                        <p className="text-xl font-bold">
                          {meetsRequirement ? "✓ PASS" : "✕ REJECT"}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Modal Footer Controls */}
            {(() => {
              const total = photoApprovals.length;
              const approvedCount = photoApprovals.filter((p) => p.isApproved === true).length;
              const unreviewedCount = photoApprovals.filter((p) => p.isApproved === null).length;
              const meetsRequirement = approvedCount >= MIN_PHOTOS_REQUIRED;

              return (
                <div className="flex items-center justify-end p-6 border-t border-cyan-500/20 bg-cyan-950/40">
                  
                  <div className="flex gap-3">
                    <button
                      type="button"
                      disabled={isSubmitting}
                      onClick={() => {
                        setSelectedUser(null);
                        setModalAction(null);
                        setPhotoApprovals([]);
                      }}
                      className="px-4 py-2 rounded-xl text-xs font-semibold bg-cyan-950/60 text-cyan-200 border border-cyan-500/30 hover:bg-cyan-900/60 transition disabled:opacity-50"
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      disabled={isSubmitting || unreviewedCount > 0}
                      onClick={handleVerificationSubmit}
                      className={`px-5 py-2 rounded-xl text-xs font-bold text-white shadow-lg transition disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 ${
                        meetsRequirement
                          ? "bg-gradient-to-r from-teal-500 to-emerald-600"
                          : "bg-gradient-to-r from-rose-500 to-red-600"
                      }`}
                    >
                      {isSubmitting
                        ? "Submitting..."
                        : unreviewedCount > 0
                        ? `Review All (${total - unreviewedCount}/${total})`
                        : meetsRequirement
                        ? `✓ Approve User `
                        : `✕ Reject User `}
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Rejected Photos Feedback Modal */}
      {selectedUser && modalAction === "rejected-view" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md overflow-y-auto">
          <div
            className="w-full max-w-2xl rounded-2xl overflow-hidden border border-rose-400/30 backdrop-blur-2xl my-8 flex flex-col max-h-[90vh]"
            style={{ background: "rgba(4, 18, 48, 0.95)" }}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-rose-500/20 bg-rose-950/40">
              <div>
                <h3 className="text-base font-bold text-white">
                  Rejection Feedback — {selectedUser.fullName || "User"}
                </h3>
                <p className="text-xs text-rose-200/70 mt-0.5">
                  {selectedUser.approvedPhotos || 0}/{(selectedUser.verificationPhotos || []).length} photos approved
                </p>
              </div>
              <button
                onClick={() => {
                  setSelectedUser(null);
                  setModalAction(null);
                }}
                className="text-rose-300 hover:text-white"
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/20">
                <p className="text-sm text-rose-200">
                  <strong>{selectedUser.approvedPhotos || 0}</strong> photo(s) passed, and{" "}
                  <strong>
                    {Math.max(
                      0,
                      (selectedUser.verificationPhotos?.length || 0) - (selectedUser.approvedPhotos || 0)
                    )}
                  </strong>{" "}
                  failed verification.
                </p>
                <p className="text-xs text-rose-200/70 mt-1">
                  The user must address the feedback below before re-applying.
                </p>
              </div>

              {/* List of Rejected Photos */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold uppercase text-white tracking-wider">
                  Photos Requiring Action:
                </h4>
                {(selectedUser.verificationPhotos || []).map((photo, idx) => {
                  if (photo.adminStatus === "approved") return null;

                  return (
                    <div key={idx} className="border border-rose-500/20 rounded-xl p-4 bg-rose-950/30">
                      <div className="flex gap-4">
                        <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden border border-rose-500/30 bg-[#020b1e]">
                          <img
                            src={photo.url}
                            alt={`Photo ${idx + 1}`}
                            className="w-full h-full object-cover opacity-70"
                          />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-sm font-semibold text-white">Photo #{idx + 1}</span>
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/20 border border-rose-500/30 text-rose-300 font-bold">
                              ✕ Rejected
                            </span>
                          </div>
                          <p className="text-xs text-rose-200/90 bg-rose-950/60 p-2.5 rounded-lg border border-rose-500/20">
                            {photo.adminFeedback || "No specific feedback was provided for this image."}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Approved Photos Reference */}
              {(selectedUser.verificationPhotos || []).some((p) => p.adminStatus === "approved") && (
                <div className="pt-4 border-t border-rose-500/20">
                  <h4 className="text-xs font-bold uppercase text-teal-300 tracking-wider mb-2">
                    Approved Photos:
                  </h4>
                  <div className="grid grid-cols-4 gap-2">
                    {selectedUser.verificationPhotos.map((photo, idx) => {
                      if (photo.adminStatus !== "approved") return null;
                      return (
                        <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border border-teal-500/30 bg-[#020b1e]">
                          <img src={photo.url} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover" />
                          <div className="absolute top-1 right-1 text-[10px] px-1 bg-teal-500/80 text-white font-bold rounded">✓</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 p-6 border-t border-rose-500/20 bg-rose-950/40">
              <button
                type="button"
                onClick={() => {
                  setSelectedUser(null);
                  setModalAction(null);
                }}
                className="px-4 py-2 rounded-xl text-xs font-semibold bg-rose-950/60 text-rose-200 border border-rose-500/30 hover:bg-rose-900/60 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SelfieApprovals;